import React, { useEffect, useState } from 'react';
import { formatDate } from 'src/components/utilities/utils';
import Loader from 'src/components/loader';
import { useNavigate, useParams } from 'react-router-dom';
import { useVideosActions } from 'src/store/videos/actions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import { useSearchParams } from "react-router-dom";

interface Props {
    videoId?: string;
    memberId?: string;
    onBack?: () => void;
}

const VideoQuizResult: React.FC<Props> = (props) => {
    const params = useParams<{ videoId: string; memberId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const videoAction = useVideosActions(dispatch);

    const videoId = props.videoId ?? params.videoId ?? '';
    const memberId = props.memberId ?? params.memberId ?? '';
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email");

    const [isLoading, setIsLoading] = useState(true);

    const { videoQuizResult, loading } = useSelector((state: RootState) => ({
        videoQuizResult: state.videos.videoQuizResult,
        loading: state.videos.loading
    }));
    console.log(videoQuizResult)
    useEffect(() => {
        setIsLoading(true);
        videoAction.getVideoQuizResultRequest({ videoId, memberId });
    }, [videoId, memberId]);

    useEffect(() => {
        if (videoQuizResult) {
            setIsLoading(false);
        }
    }, [videoQuizResult]);

    const result = videoQuizResult;
    const video = result?.video;
    const quizzes = result?.quizzes ?? [];

    const getStatusBadgeClass = (status: string) => {
        const s = status?.toLowerCase();
        if (s === 'pass' || s === 'completed' || s === 'passed') return 'bg-green-100 text-green-700';
        if (s === 'fail' || s === 'failed' || s === 'not started') return 'bg-red-100 text-red-700';
        return 'bg-yellow-100 text-yellow-700';
    };

    const getQuizStatus = (quiz: any): string => {
        if (quiz.totalAttempts === 0) return 'Not Attempted';
        if (quiz.passedAttempts > 0) return 'Pass';
        return 'Fail';
    };

    const getVideoStatus = (v: any): string => {
        if (!v || v.percentageWatched == null) return 'Not Started';
        if (v.percentageWatched >= 100) return 'Completed';
        if (v.percentageWatched > 0) return 'In progress';
        return 'Not Started';
    };

    const totalAttempts = quizzes.reduce((sum: number, q: any) => sum + (q.totalAttempts ?? 0), 0);
    const quizzesPassed = quizzes.filter((q: any) => q.passedAttempts > 0).length;

    return (
        <div className="dashboard">

            <div className="flex mt-1">
                <h2 className="font-semibold">Video Quiz Progress</h2>
            </div>
            <p className="text-sm text-gray-600 mt-2">
                <strong>User:</strong> {email ?? "—"}
            </p>
            <div className="mt-4 members-list">

                {isLoading ? (
                    <div className="flex justify-center py-16">
                        <Loader />
                    </div>

                ) : loading ? (
                    <Loader />

                ) : (
                    <>
                        {/* Video info header */}
                        <div className="py-4 border-b border-gray-100 md:flex justify-between items-center">
                            <div>
                                <h2 className="font-semibold">{video?.videoName}</h2>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                    <i className="fa fa-clock-o"></i>Total Time Watched -
                                    <span>{video?.totalTimeWatchedInFormat ?? '—'}</span>
                                </div>
                                <p className="text-sm text-gray-400 mt-1">
                                    {video?.lastView ? `Last viewed - ${formatDate(video.lastView)}` : 'Not yet viewed'}
                                </p>
                            </div>
                        </div>

                        {/* Summary tiles */}
                        <div className="flex flex-wrap mt-5 dashboard-tiles">

                            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
                                <div className="dashboard-card flex-items justify-center">
                                    <div className="flex-items w-full p-4">
                                        <div className="flex flex-col">
                                            <span className="text-2xl font-semibold">
                                                {video?.percentageWatched ?? 0}%
                                            </span>
                                            <span className="mb-2">Watched</span>
                                        </div>
                                        <div>
                                            <div className="w-10 h-10 flex items-center justify-center bg-light-2 rounded-full">
                                                <i className="fa fa-check-square-o"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
                                <div className="dashboard-card flex-items justify-center">
                                    <div className="flex-items w-full p-4">
                                        <div className="flex flex-col">
                                            <span className="text-2xl font-semibold">
                                                {quizzes.length}
                                            </span>
                                            <span className="mb-2">Total Quizzes</span>
                                        </div>
                                        <div>
                                            <div className="w-10 h-10 flex items-center justify-center bg-light-5 rounded-full">
                                                <i className="fa fa-list-alt"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
                                <div className="dashboard-card flex-items justify-center">
                                    <div className="flex-items w-full p-4">
                                        <div className="flex flex-col">
                                            <span className="text-2xl font-semibold">
                                                {totalAttempts}
                                            </span>
                                            <span className="mb-2">Total Attempts</span>
                                        </div>
                                        <div>
                                            <div className="w-10 h-10 flex items-center justify-center bg-light-3 rounded-full">
                                                <i className="fa fa-repeat"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
                                <div className="dashboard-card flex-items justify-center">
                                    <div className="flex-items w-full p-4">
                                        <div className="flex flex-col">
                                            <span className="text-2xl font-semibold">
                                                {quizzesPassed}
                                            </span>
                                            <span className="mb-2">Quizzes Passed</span>
                                        </div>
                                        <div>
                                            <div className="w-10 h-10 flex items-center justify-center bg-light-4 rounded-full">
                                                <i className="fa fa-star"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
                                <div className="dashboard-card flex-items justify-center">
                                    <div className="flex-items w-full p-4">
                                        <div className="flex flex-col">
                                            <span
                                                className={`text-xs font-medium px-2.5 py-0.5 rounded dark:bg-yellow-900 dark:text-yellow-300 single-line-badge ${getStatusBadgeClass(
                                                    getVideoStatus(video)
                                                )}`}>
                                                {getVideoStatus(video)}
                                            </span>
                                            <span className="mb-2 mt-2">Video Status</span>
                                        </div>
                                        <div>
                                            <div className="w-10 h-10 flex items-center justify-center bg-light-1 rounded-full">
                                                <i className="fa fa-info-circle"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Quizzes table */}
                        <div className="py-4 border-b border-gray-100 md:flex justify-between items-center">
                            <h2 className="font-semibold">Quizzes for This Video</h2>
                        </div>

                        <div className="flex flex-col justify-center h-full mt-4">
                            <div className="w-full mx-auto bg-white shadow-card rounded-lg relative">
                                <div className="p-3">
                                    <div className="overflow-x-auto">
                                        <table className="table-auto w-full border">
                                            <thead className="text-xs font-semibold uppercase bg-gray-50">
                                                <tr>
                                                    <th className="p-2"><div className="font-semibold text-left">Quiz Name</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Video</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Attempts</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Passed</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Failed</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Highest Score</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Status</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Last Attempt</div></th>
                                                    <th className="p-2"><div className="font-semibold text-left">Actions</div></th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-sm divide-y divide-gray-100">
                                                {quizzes.length > 0 ? (
                                                    quizzes.map((quiz: any) => {
                                                        const status = getQuizStatus(quiz);
                                                        return (
                                                            <tr key={quiz.quizId}>
                                                                <td className="p-2"><div className="text-left font-medium">{quiz.quizName}</div></td>
                                                                <td className="p-2"><div className="text-left">{quiz.videoTitle || '—'}</div></td>
                                                                <td className="p-2">
                                                                    <div className="text-left">
                                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs">
                                                                            {quiz.totalAttempts}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="p-2">
                                                                    <div className="text-left">
                                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-700 font-semibold text-xs">
                                                                            {quiz.passedAttempts}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="p-2">
                                                                    <div className="text-left">
                                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 text-red-700 font-semibold text-xs">
                                                                            {quiz.failedAttempts}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="p-2">
                                                                    <div className="text-left">
                                                                        {quiz.highestScore != null
                                                                            ? `${Math.round(quiz.highestScore)}%`
                                                                            : '—'}
                                                                    </div>
                                                                </td>
                                                                <td className="p-2">
                                                                    <span className={`text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-yellow-900 dark:text-yellow-300 single-line-badge ${getStatusBadgeClass(status)}`}>
                                                                        {status}
                                                                    </span>
                                                                </td>
                                                                <td className="p-2">
                                                                    <div className="text-left">
                                                                        {quiz.lastAttemptedDate ? formatDate(quiz.lastAttemptedDate) : '—'}
                                                                    </div>
                                                                </td>
                                                                <td className="p-2">
                                                                    <button
                                                                        onClick={() => {
                                                                            const url = `/quizAttemptDetails/video/${videoId}/${memberId}/${quiz.quizId}`;
                                                                            window.open(window.location.origin + url, '_blank');
                                                                        }}
                                                                        className="btn-primary w-auto mt-0 mr-0 borderonly-btn">
                                                                        View Attempts
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan={9} className="p-4 text-center text-gray-500">
                                                            No quiz data found for this video.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default VideoQuizResult;